import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Header = ({ className = '' }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProgressVisible, setIsProgressVisible] = useState(false);
  const [progressState, setProgressState] = useState({ step: 0, total: 0, operation: '' });
  const [securityStatus, setSecurityStatus] = useState({
    walletSync: 'connected',
    keyHealth: 'secure',
    blockchainStatus: 'synced'
  });
  const location = useLocation();

  const navigationItems = [
    {
      label: 'Dashboard',
      path: '/dashboard',
      icon: 'LayoutDashboard',
      tooltip: 'Overview and quick actions for credential management'
    },
    {
      label: 'Digital Wallet',
      path: '/digital-wallet',
      icon: 'Wallet',
      tooltip: 'Store and organize your verifiable credentials'
    },
    {
      label: 'Issue Credentials',
      path: '/credential-issuance',
      icon: 'FileText',
      tooltip: 'Create and issue new verifiable credentials'
    },
    {
      label: 'Verify Credentials',
      path: '/credential-verification',
      icon: 'Shield',
      tooltip: 'Validate and verify credential authenticity'
    },
    {
      label: 'Privacy Settings',
      path: '/privacy-settings',
      icon: 'Settings',
      tooltip: 'Configure privacy controls and consent management'
    }
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleNavigation = (path) => {
    window.location.href = path;
    closeMobileMenu();
  };

  const getSecurityStatusColor = () => {
    const statuses = Object.values(securityStatus);
    if (statuses?.every(status => status === 'secure' || status === 'connected' || status === 'synced')) {
      return 'text-success';
    }
    if (statuses?.some(status => status === 'warning')) {
      return 'text-warning';
    }
    return 'text-error';
  };

  const getSecurityStatusIcon = () => {
    const statuses = Object.values(securityStatus);
    if (statuses?.every(status => status === 'secure' || status === 'connected' || status === 'synced')) {
      return 'ShieldCheck';
    }
    if (statuses?.some(status => status === 'warning')) {
      return 'ShieldAlert';
    }
    return 'ShieldX';
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e?.key === 'Escape' && isMobileMenuOpen) {
        closeMobileMenu();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-[1000] bg-background border-b border-border ${className}`}>
        <div className="w-full px-4 lg:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <div 
                className="flex items-center cursor-pointer"
                onClick={() => handleNavigation('/dashboard')}
              >
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center mr-3">
                  <Icon name="Shield" size={20} color="white" />
                </div>
                <span className="text-xl font-semibold text-foreground">VerifyChain</span>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {navigationItems?.map((item) => (
                <button
                  key={item?.path}
                  onClick={() => handleNavigation(item?.path)}
                  className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                    location?.pathname === item?.path
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                  title={item?.tooltip}
                >
                  <Icon name={item?.icon} size={16} className="mr-2" />
                  {item?.label}
                </button>
              ))}
            </nav>

            {/* Progress Indicator */}
            {isProgressVisible && (
              <div className="hidden lg:flex items-center ml-4 px-3 py-1 bg-muted rounded-full">
                <div className="w-4 h-4 mr-2">
                  <div className="progress-ring w-4 h-4">
                    <svg className="w-4 h-4" viewBox="0 0 100 100">
                      <circle
                        className="progress-ring-circle stroke-primary"
                        strokeWidth="8"
                        fill="transparent"
                        r="45"
                        cx="50"
                        cy="50"
                        style={{
                          strokeDashoffset: 283 - (283 * progressState?.step) / progressState?.total
                        }}
                      />
                    </svg>
                  </div>
                </div>
                <span className="text-xs text-muted-foreground">
                  {progressState?.operation} ({progressState?.step}/{progressState?.total})
                </span>
              </div>
            )}

            {/* Security Status & Mobile Menu */}
            <div className="flex items-center space-x-3">
              {/* Security Status Indicator */}
              <div className="relative group">
                <button className={`p-2 rounded-full hover:bg-muted transition-colors ${getSecurityStatusColor()}`}>
                  <Icon name={getSecurityStatusIcon()} size={20} />
                </button>
                
                {/* Security Status Tooltip */}
                <div className="absolute right-0 top-full mt-2 w-64 bg-popover border border-border rounded-lg shadow-modal opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[1020]">
                  <div className="p-3">
                    <h4 className="font-medium text-sm mb-2">Security Status</h4>
                    <div className="space-y-2 text-xs">
                      <div className="flex justify-between">
                        <span>Wallet Sync:</span>
                        <span className={securityStatus?.walletSync === 'connected' ? 'text-success' : 'text-warning'}>
                          {securityStatus?.walletSync}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Key Health:</span>
                        <span className={securityStatus?.keyHealth === 'secure' ? 'text-success' : 'text-warning'}>
                          {securityStatus?.keyHealth}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Blockchain:</span>
                        <span className={securityStatus?.blockchainStatus === 'synced' ? 'text-success' : 'text-warning'}>
                          {securityStatus?.blockchainStatus}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={toggleMobileMenu}
                aria-label="Toggle navigation menu"
              >
                <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={24} />
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Progress Indicator */}
        {isProgressVisible && (
          <div className="lg:hidden border-t border-border bg-muted/50 px-4 py-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-4 h-4 mr-2 animate-verification-scan">
                  <Icon name="Loader2" size={16} className="text-primary" />
                </div>
                <span className="text-xs text-muted-foreground">{progressState?.operation}</span>
              </div>
              <span className="text-xs text-muted-foreground">
                {progressState?.step}/{progressState?.total}
              </span>
            </div>
          </div>
        )}
      </header>
      {/* Mobile Navigation Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-[1030] lg:hidden">
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm" onClick={closeMobileMenu} />
          <div className="fixed top-16 left-0 right-0 bottom-0 bg-background border-t border-border">
            <nav className="p-4">
              <div className="space-y-2">
                {navigationItems?.map((item) => (
                  <button
                    key={item?.path}
                    onClick={() => handleNavigation(item?.path)}
                    className={`w-full flex items-center px-4 py-3 rounded-lg text-left transition-colors duration-200 ${
                      location?.pathname === item?.path
                        ? 'bg-primary text-primary-foreground'
                        : 'text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name={item?.icon} size={20} className="mr-3" />
                    <div>
                      <div className="font-medium">{item?.label}</div>
                      <div className="text-xs text-muted-foreground mt-1">{item?.tooltip}</div>
                    </div>
                  </button>
                ))}
              </div>

              {/* Mobile Security Status */}
              <div className="mt-6 p-4 bg-muted rounded-lg">
                <h4 className="font-medium text-sm mb-3 flex items-center">
                  <Icon name={getSecurityStatusIcon()} size={16} className={`mr-2 ${getSecurityStatusColor()}`} />
                  Security Status
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Wallet Sync:</span>
                    <span className={securityStatus?.walletSync === 'connected' ? 'text-success' : 'text-warning'}>
                      {securityStatus?.walletSync}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Key Health:</span>
                    <span className={securityStatus?.keyHealth === 'secure' ? 'text-success' : 'text-warning'}>
                      {securityStatus?.keyHealth}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Blockchain:</span>
                    <span className={securityStatus?.blockchainStatus === 'synced' ? 'text-success' : 'text-warning'}>
                      {securityStatus?.blockchainStatus}
                    </span>
                  </div>
                </div>
              </div>
            </nav>
          </div>
        </div>
      )}
      {/* Quick Action Menu */}
      <div className="fixed bottom-6 right-6 z-[1040]">
        <div className="relative group">
          <Button
            variant="default"
            size="icon"
            className="w-14 h-14 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 bg-primary hover:bg-primary/90"
          >
            <Icon name="Plus" size={24} />
          </Button>
          
          {/* Quick Actions Panel */}
          <div className="absolute bottom-full right-0 mb-2 w-48 bg-popover border border-border rounded-lg shadow-modal opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
            <div className="p-2">
              <button
                onClick={() => handleNavigation('/credential-issuance')}
                className="w-full flex items-center px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors"
              >
                <Icon name="FileText" size={16} className="mr-2" />
                Issue Credential
              </button>
              <button
                onClick={() => handleNavigation('/credential-verification')}
                className="w-full flex items-center px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors"
              >
                <Icon name="Shield" size={16} className="mr-2" />
                Verify Credential
              </button>
              <button
                onClick={() => handleNavigation('/digital-wallet')}
                className="w-full flex items-center px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors"
              >
                <Icon name="Share" size={16} className="mr-2" />
                Share Credential
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;